import { Injectable, inject } from '@angular/core';
import { LendingTypeCode } from '@shared/models';
import { FilterPreferences, defaultFilterPreferences } from '@shared/models/filter-preferences';
import { AuthorizeService } from '@shared/services';
import { ClubHubDataService } from '@shared/services/clubhub-data.service';
import { Observable, of, shareReplay } from 'rxjs';

/**
 * The filter and sort preferences a search should run with: the broker's own resolved over their firm's, already
 * merged by the API, with the firm's lock flags alongside so the UI can render a locked control read-only.
 *
 * Deliberately read-only, and deliberately separate from the My account `FilterPreferencesService`. Changes a
 * broker makes during a search must never write back to their saved preferences, and keeping the write methods out
 * of the search journey's reach is the cheapest way to guarantee that. The other service's cache is also cleared
 * when its page is destroyed, so sharing it would leave this one empty in most flows.
 */
@Injectable({ providedIn: 'root' })
export class SearchFilterPreferencesService {
  private readonly dataService = inject(ClubHubDataService);
  private readonly authService = inject(AuthorizeService);

  /**
   * One shared request per lending type rather than a re-fetch.
   *
   * A search reads preferences from more than one place - the sort as the page initialises, the filters once
   * products land - and the resolved set must not change between those reads or the two would disagree.
   */
  private readonly resolved = new Map<LendingTypeCode, Observable<FilterPreferences>>();

  public getPreferences(lendingTypeCode: LendingTypeCode): Observable<FilterPreferences> {
    if (!this.authService.isLoggedIn) {
      return of(defaultFilterPreferences);
    }

    const cached = this.resolved.get(lendingTypeCode);
    if (cached) {
      return cached;
    }

    // No loading indicator: this resolves alongside a search that has its own, and a spinner for a settings fetch
    // the broker did not ask for would be noise.
    const request = this.dataService
      .get<FilterPreferences>(`FilterPreferences/${lendingTypeCode}/Effective`, { loading: false })
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));

    this.resolved.set(lendingTypeCode, request);

    return request;
  }

  /** Dropped when a search journey ends so the next one re-reads, picking up any change the firm has made. */
  public reset(): void {
    this.resolved.clear();
  }
}
